// PageNavigation.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DicesIcon, Wrench, Palette, FolderHeart, Repeat, LayoutGrid } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import ToolsMenuModal from './ToolsMenuModal';

// Navigation items configuration
const NAV_ITEMS_BASE = {
  home: {
    key: 'nav.random',
    path: '/',
    icon: <DicesIcon size={16} />,
    color: 'from-pink-400 to-rose-500',
    hoverColor: 'hover:from-pink-300 hover:to-rose-400',
    isPage: true
  },
  palettes: {
    key: 'nav.palettes',
    path: '/palettes',
    icon: <Palette size={16} />,
    color: 'from-emerald-400 to-teal-500',
    hoverColor: 'hover:from-emerald-300 hover:to-teal-400',
    isPage: true
  },
  collection: {
    key: 'nav.collection',
    path: '/collection',
    icon: <FolderHeart size={16} />,
    color: 'from-yellow-400 to-orange-500',
    hoverColor: 'hover:from-yellow-300 hover:to-orange-400',
    isPage: true
  },
    converter: {
    key: 'nav.converter',
    path: '/brand-converter',
    icon: <Repeat size={16} />,
    color: 'from-blue-400 to-cyan-500',
    hoverColor: 'hover:from-blue-400 hover:to-cyan-500',
    isPage: true
  },
  gallery: {
    key: 'nav.gallery',
    path: '/gallery',
    icon: <LayoutGrid size={16} />,
    color: 'from-purple-400 to-pink-500',
    hoverColor: 'hover:from-purple-300 hover:to-pink-400',
    isPage: true
  },
  tools: {
    key: 'nav.tools',
    path: null,
    icon: <Wrench size={16} />,
    color: 'from-purple-400 to-violet-500',
    hoverColor: 'hover:from-purple-300 hover:to-violet-400',
    isPage: false
  }
};

// Tool pages that should highlight the "Tools" nav item
const TOOL_PATHS = ['/picker', '/hex-finder', '/shades'];

export default function PageNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [showToolsModal, setShowToolsModal] = useState(false);

  const handleNavClick = (navKey) => {
    const navItem = NAV_ITEMS_BASE[navKey];

    if (navItem.isPage) {
      navigate(navItem.path);
    } else if (navKey === 'tools') {
      setShowToolsModal(true);
    }
  };

  // Check if current path is a tool page
  const isToolsActive = TOOL_PATHS.includes(location.pathname);

  // Check if a nav item is active
  const isNavActive = (navKey) => {
    const navItem = NAV_ITEMS_BASE[navKey];
    if (navKey === 'tools') {
      return isToolsActive;
    }
    if (navKey === 'home') {
      // Home is active if we're at root without ?page=legal
      return location.pathname === '/' && !location.search.includes('page=legal');
    }
    return location.pathname === navItem.path;
  };

  // Create nav items with translated titles
  const NAV_ITEMS = Object.fromEntries(
    Object.entries(NAV_ITEMS_BASE).map(([navKey, data]) => ([
      navKey,
      { ...data, title: t(data.key) }
    ]))
  );

  return (
    <>
      <nav className="backdrop-blur-sm z-40">
        <div className="max-w-5xl mx-auto px-4 py-6">

          {/* Mobile Navigation - Beautiful Cards */}
          <div className="grid grid-cols-3 lg:hidden gap-2">
            {Object.entries(NAV_ITEMS).map(([navKey, navData]) => {
              const isActive = isNavActive(navKey);

              return (
                <button
                  key={navKey}
                  onClick={() => handleNavClick(navKey)}
                  className={`
                    relative overflow-hidden px-2 py-2 rounded-xl font-small transition-all duration-500 transform max-w-[100px]"
                    ${isActive
                      ? `bg-gradient-to-br ${navData.color} text-white shadow-2xl shadow-pink-200/50`
                      : `bg-white text-gray-600 hover:text-white hover:bg-gradient-to-br ${navData.hoverColor} shadow-lg hover:shadow-xl hover border border-pink-100`
                    }
                  `}
                >
                  <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/20 animate-pulse"></div>
                  <div className="absolute bottom-2 left-2 w-4 h-4 rounded-full bg-white/10"></div>

                  <div className="relative z-10">
                    <div className="font-bold text-[12px] flex items-center gap-2">
                      {navData.icon}
                      <span>{navData.title}</span>
                      {navData.badge && (
                        <span className="text-[9px] px-1 py-0.5 rounded-full bg-yellow-500 text-white leading-none">
                          {navData.badge}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Desktop Navigation - Floating Pills */}
          <div className="hidden lg:flex justify-center">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-2 shadow-xl shadow-pink-100/50 border border-pink-100/50">
              <div className="flex gap-4">
                {Object.entries(NAV_ITEMS).map(([navKey, navData]) => {
                  const isActive = isNavActive(navKey);

                  return (
                    <button
                      key={navKey}
                      onClick={() => handleNavClick(navKey)}
                      className={`
                        relative overflow-hidden px-8 py-4 rounded-2xl font-medium transition-all duration-500 flex items-center gap-3 group
                        ${isActive
                          ? `bg-gradient-to-r ${navData.color} text-white shadow-lg transform scale-110`
                          : `text-gray-600 hover:scale-105`
                        }
                      `}
                    >
                      {!isActive && (
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      )}

                      <span className="relative z-10 font-bold flex items-center gap-2">
                        {navData.icon}
                        {navData.title}
                        {navData.badge && (
                          <span className="text-[9px] px-1 py-0.5 rounded-full bg-yellow-500 text-white leading-none">
                            {navData.badge}
                          </span>
                        )}
                      </span>

                      {isActive && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 animate-pulse"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

        </div>
      </nav>

      {/* Tools Menu Modal */}
      <ToolsMenuModal
        isOpen={showToolsModal}
        onClose={() => setShowToolsModal(false)}
      />
    </>
  );
}
